import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
	dxtToLocalServerTime,
	getMomentTz,
} from "@dexteel/mesf-core";
import { Button, Grid2 as Grid, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { isNil } from "lodash-es";
import React, { useEffect, useState } from "react";
import { UnLinkedHeat } from "../../models/UnLinkedHeat";
import {
	linkCasterProcess,
	searchUnlinkedHeats,
} from "../../repositories/ProductionRepository";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	process: {
		ProcessId: number;
		LadleOpen: Date | null;
		HeatName: string | null;
	} | null;
};

export const LinkCasterProcess = ({ show, onHide, process }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [title, setTitle] = useState("");
	const [unlinkedHeats, setUnLinkedHeats] = useState<UnLinkedHeat[]>([]);
	const [selectedHeat, setSelectedHeat] = useState<any>(null);
	const [searchText, setSearchText] = useState("");
	const [error, setError] = useState("");
	const [showWarningModal, setShowWarningModal] = useState(false);

	const onSubmit = async () => {
		try {
			if (selectedHeat === null) {
				setError("Please select a heat");
				return;
			}
			if (isNil(process)) return;

			if (selectedHeat.IsLinked === 1) {
				setShowWarningModal(true);
			} else {
				await proceedWithLinking();
			}
		} catch (e: any) {
			setError(e.message);
		}
	};

	const proceedWithLinking = async () => {
		try {
			setIsSubmitLoading(true);
			const response = await linkCasterProcess(
				process?.ProcessId as number,
				selectedHeat.HeatId,
			);
			if (response.ok) {
				onHide(true);
			} else {
				setError(response.message);
			}
		} catch (e: any) {
			setError(e.message);
		} finally {
			setIsSubmitLoading(false);
			setShowWarningModal(false);
		}
	};

	const handleSearchChange = async () => {
		if (!process) return;
		const heats = await searchUnlinkedHeats(
			searchText,
			process.LadleOpen as Date,
		);
		if (heats.ok && heats.data?.tables?.[0]?.rows) {
			setUnLinkedHeats(heats.data.tables[0].rows);
		}
	};

	const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
		setSearchText(value);
	};

	const handleKeyDown = async (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			await handleSearchChange();
		}
	};

	const handleChange = (
		event: React.ChangeEvent<{}>,
		newValue: UnLinkedHeat | null,
	) => {
		setSelectedHeat(newValue as any);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setSelectedHeat(null);
				if (process === undefined || process === null) return;
				setTitle(
					`Link Caster Process / Ladle Open: ${dxtToLocalServerTime(
						`${process.LadleOpen}`,
						"HH:mm",
					)}
					 Heat: ${process.HeatName ? process.HeatName : ""}`,
				);
				const heats = await searchUnlinkedHeats("", process.LadleOpen as Date);
				if (
					heats.ok &&
					heats.data &&
					heats.data.tables &&
					heats.data.tables[0].rows
				) {
					setUnLinkedHeats(heats.data.tables[0].rows);
				}
			})();
		}
	}, [show]);

	return (
		<>
			{show && <HelmetDexteel title={`Link Heat`} />}
			<MesfModal
				maxWidth="xl"
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="linkCasterProcess"
				title={title}
			>
				<MesfModal.Content dividers>
					<Grid container spacing={1} alignItems="center">
						<Grid size={{ xs: 12 }}>
							<Autocomplete
								options={unlinkedHeats}
								getOptionLabel={(option) => option.HeatName}
								value={selectedHeat}
								onChange={handleChange}
								inputValue={searchText}
								onInputChange={handleInputChange}
								renderOption={(props, option) => (
									<li {...props}>
										<div style={{ display: "flex", alignItems: "center" }}>
											<span>{option.HeatName}</span>
											{option.IsLinked === 1 && (
												<span style={{ color: "red", marginLeft: "10px" }}>
													(linked)
												</span>
											)}
										</div>
									</li>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label={searchText ? "Heat" : "Search Heat"}
										placeholder="Search Heat"
										variant="outlined"
										fullWidth
										onKeyDown={handleKeyDown}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</MesfModal.Content>

				<MesfModal.Actions>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid size={{ md: 6, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide(true)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid size={{ md: 6, xs: 12 }} style={{ margin: 0 }}>
							<ButtonWithLoading
								fullWidth
								isLoading={isSubmitLoading}
								disabled={isSubmitLoading}
								variant="contained"
								color="primary"
								onClick={onSubmit}
							>
								Save
							</ButtonWithLoading>
						</Grid>
					</Grid>
				</MesfModal.Actions>
			</MesfModal>

			<MesfModal
				open={showWarningModal}
				handleClose={() => setShowWarningModal(false)}
				title="Warning"
				id="warningModal"
			>
				<MesfModal.Content>
					<p style={{ textAlign: "center" }}>
						This heat is already linked in another process. Do you want to
						proceed?
					</p>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button
						fullWidth
						variant="contained"
						color="inherit"
						onClick={() => setShowWarningModal(false)}
					>
						Cancel
					</Button>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						onClick={proceedWithLinking}
					>
						Proceed
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
